import type { AccessScope, Permission } from '@whn/shared';
import { Loader2 } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './auth-context';

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="flex h-full min-h-screen items-center justify-center" role="status">
      <Loader2 className="size-6 animate-spin text-ink-subtle" aria-hidden="true" />
      <span className="sr-only-focusable">{label}</span>
    </div>
  );
}

/**
 * Gate for authenticated areas. While the session is still being resolved we
 * render a spinner rather than redirecting — otherwise a hard refresh would
 * bounce a signed-in user to the login screen before `/auth/me` answered.
 */
export function RequireAuth() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner label="Checking your session" />;
  if (!user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Outlet />;
}

/** Keeps signed-in users away from the sign-in and sign-up screens. */
export function RequireAnonymous() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner label="Checking your session" />;
  if (user) {
    // The moment a session appears this guard re-renders and redirects, which
    // races the sign-in page's own navigate(). Honouring `from` here means the
    // user still lands on the page they originally asked for, whichever of the
    // two redirects wins.
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? '/'} replace />;
  }
  return <Outlet />;
}

/**
 * Route-level capability gate, optionally requiring a minimum reach: a screen
 * that lists other people needs TEAM, not merely the OWN scope that lets
 * someone read their own record.
 *
 * This hides a screen the API would refuse anyway; it is a courtesy to the
 * user, never the security boundary.
 */
export function RequirePermission({
  permission,
  minScope = 'OWN',
}: {
  permission: Permission;
  minScope?: AccessScope;
}) {
  const { canAtScope } = useAuth();
  if (!canAtScope(permission, minScope)) return <Navigate to="/" replace />;
  return <Outlet />;
}
